import { createApp } from 'vue'
import type { Store } from 'pinia'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { IonicVue } from '@ionic/vue'
import VueApp from './App.vue'
import router from './router'
import { sentryPlugin } from './plugins/sentry'
import { setupAppPlugin } from './plugins/setupApp'
import { swiperPlugin } from './plugins/swiper'
import { lottiePlugin } from './plugins/Vue3Lottie.client'
import { setupCspSafeBootstraps } from './plugins/cspBootstrap'
import { initialiseCanonicalTracking } from './composables/canonicalTracking'
import { webAnalyticsPlugin } from './plugins/webAnalytics'
import { googleMapsLoader } from './plugins/googlemaps'
import * as Sentry from '@sentry/capacitor'

// Styling
import 'virtual:uno.css'

/* Adyen styling */
import '@adyen/adyen-web/styles/adyen.css'

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css'

/* Theme variables (overrides) */
import '~/theme/main.scss'

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css'
import '@ionic/vue/css/structure.css'
import '@ionic/vue/css/typography.css'

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css'
import '@ionic/vue/css/float-elements.css'
import '@ionic/vue/css/text-alignment.css'
import '@ionic/vue/css/text-transformation.css'
import '@ionic/vue/css/flex-utils.css'
import '@ionic/vue/css/display.css'

import customTransitionAnimation from './ionic-transitions/customTransitionAnimation'
import { useAuthStore, useNavigationStore, useUserStore, useAppStatusStore } from './store'
import { Preferences } from '@capacitor/preferences'
import type { URLOpenListenerEvent } from '@capacitor/app'
import { App } from '@capacitor/app'
import { middlewareDeepLinkMobile } from './router/middleware/deepLink'
import type { NotificationOpenedCallbackEvent } from './composables/notifications'
import type { RouteLocationRaw } from 'vue-router'
import { SystemBars, SystemBarsStyle, SystemBarType } from '@capacitor/core'
import type { STORE_ID } from './constants/store'
import type { AppStatusState } from './types/appStatus'
import { safeHtmlDirective } from './directives/safeHtml.ts'
import { trustedHtmlDirective } from './directives/trustedHtml.ts'
import { initialiseStripePaymentSheet } from './plugins/stripe'

setupCspSafeBootstraps()
initialiseCanonicalTracking()

// State Management
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(({ store }) => {
  store.$router = markRaw(router)
})
const stripeBootstrapPromise = initialiseStripePaymentSheet(pinia)

const getIonicConfig = () => {
  let config: any = {
    mode: 'ios',
    // Enable html in text display of ionic components such as ion-alert, ion-infinite-scroll-content,
    // ion-loading, ion-refresher-content, and ion-toast
    // These strings are added to the DOM using innerHTML and must be properly sanitized by the developer
    innerHTMLTemplatesEnabled: true
  }

  if (isPlatform('ios') && isPlatform('mobileweb')) {
    const animation = customTransitionAnimation

    config = {
      ...config,
      navAnimation: animation,
      swipeBackEnabled: false
    }
  } else if (isPlatform('android') && isPlatform('mobileweb')) {
    config = {
      ...config,
      swipeBackEnabled: false
    }
  }

  return config
}

const app = createApp(VueApp)
  .use(sentryPlugin)
  .use(IonicVue, getIonicConfig())
  .use(pinia)
  .use(router)
  .use(setupAppPlugin)
  .use(swiperPlugin)
  .use(lottiePlugin)
  .use(webAnalyticsPlugin)
  .use(googleMapsLoader)

// --- State variable to track router readiness ---
let isRouterReady = false
// --- Store potential initial URL from appUrlOpen ---
let initialRouteLocation: RouteLocationRaw | null = null

function handleDeepLinkNavigation(route: RouteLocationRaw) {
  Sentry.addBreadcrumb({
    category: 'deep-link',
    message: `Navigating to deep link route: ${route.toString()}`,
    level: 'debug',
    data: { route: route.toString() }
  })
  router.push(route)
}

function handleForceUpdateNavigation(route: RouteLocationRaw) {
  Sentry.addBreadcrumb({
    category: 'force-update',
    message: `Navigating to force update route: ${route.toString()}`,
    level: 'debug',
    data: { route: route.toString() }
  })
  // Use a replace to prevent the user from being able to go back
  router.replace(route)
}

function initUrlOpenListener() {
  // Configure App for native

  if (isPlatform('capacitor')) {
    App.addListener('appUrlOpen', async function (event: URLOpenListenerEvent): Promise<void> {
      const url = new URL(event.url, window.location.origin)
      Sentry.addBreadcrumb({
        category: 'deep-link',
        message: `App URL Opened: ${event.url}`,
        level: 'debug'
      })
      if (url.pathname.startsWith('/order-at-table') || url.pathname.startsWith('/applink')) {
        const navigationStore = useNavigationStore()
        navigationStore.universalLinkTarget = event.url // Store the target URL in the navigation store
        // Replace the current history entry with /app-reset.
        // The user won't be able to go "back" to the page that was active before the deep link.
        Sentry.addBreadcrumb({
          category: 'deep-link',
          message: `Deep link navigation: /app-reset'`,
          data: { redirect: '/app-reset' },
          level: 'debug'
        })

        const processedUrl = '/app-reset'
        // If the router is already ready, navigate immediately.
        if (isRouterReady) {
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Router is already ready, handling deep link immediately: ${processedUrl}`,
            level: 'debug',
            data: { route: processedUrl }
          })
          handleDeepLinkNavigation(processedUrl)
        } else {
          // If router is not yet ready, store the URL.
          // This handles the cold start scenario where the event fires very early.
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Router not yet ready, queuing deep link: ${processedUrl}`,
            level: 'debug',
            data: { route: processedUrl }
          })
          initialRouteLocation = processedUrl
        }
        return // Stop further processing in this instance
      } else {
        const redirect = await middlewareDeepLinkMobile(event.url, 'applink')
        try {
          const processedRouteLocation = redirect
          // If the router is already ready, navigate immediately.
          if (isRouterReady) {
            Sentry.addBreadcrumb({
              category: 'deep-link',
              message: `Router is already ready, handling deep link immediately: ${processedRouteLocation}`,
              level: 'debug',
              data: { route: processedRouteLocation }
            })
            handleDeepLinkNavigation(processedRouteLocation)
          } else {
            // If router is not yet ready, store the URL.
            // This handles the cold start scenario where the event fires very early.
            Sentry.addBreadcrumb({
              category: 'deep-link',
              message: `Router not yet ready, queuing deep link: ${processedRouteLocation}`,
              level: 'debug',
              data: { route: processedRouteLocation }
            })
            initialRouteLocation = processedRouteLocation
          }
          Sentry.captureMessage('Deep link navigation: /applink', 'info')
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              deep_link_error: true,
              redirect_path: redirect.toString(),
              original_url: event.url
            },
            level: 'error',
            extra: {
              /* ... */
            }
          })
        }
      }
    })
  }
}

function initPushOpenListener() {
  if (isPlatform('capacitor')) {
    const { setOnNotificationOpenedListener, setOnUrlActionListener, setOnNotificationOpenedWhenTerminatedListener } =
      useSalesforceNotifications()

    setOnNotificationOpenedWhenTerminatedListener(async (event: { path: string }) => {
      Sentry.addBreadcrumb({
        category: 'deep-link',
        message: `Push notification opened when terminated: ${event.path}`,
        level: 'debug',
        data: { path: event.path }
      })
      if (event.path) {
        const redirect = await middlewareDeepLinkMobile(event.path, 'push-link')
        Sentry.addBreadcrumb({
          category: 'deep-link',
          message: `Redirecting from Push notification opened when terminated: ${event.path}`,
          level: 'debug',
          data: { redirect }
        })
        if (isRouterReady) {
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Router is already ready, handling deep link immediately: ${redirect}`,
            level: 'debug',
            data: { route: redirect }
          })
          handleDeepLinkNavigation(redirect)
        } else {
          // If router is not yet ready, store the URL.
          // This handles the cold start scenario where the event fires very early.
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Router not yet ready, queuing deep link: ${redirect}`,
            level: 'debug',
            data: { route: redirect }
          })
          initialRouteLocation = redirect
        }
      }
    })

    setOnNotificationOpenedListener((event: NotificationOpenedCallbackEvent) => {
      Sentry.addBreadcrumb({
        category: 'deep-link',
        message: `Push notification opened: ${event.values?.path}`,
        level: 'debug',
        data: { path: event.values?.path }
      })
      if (event.values?.path) {
        middlewareDeepLinkMobile(event.values.path, 'push-link').then((redirect) => {
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Redirecting from Push notification opened: ${event.values?.path}`,
            level: 'debug',
            data: { redirect }
          })
          if (isRouterReady) {
            Sentry.addBreadcrumb({
              category: 'deep-link',
              message: `Router is already ready, handling deep link immediately: ${redirect}`,
              level: 'debug',
              data: { route: redirect }
            })
            handleDeepLinkNavigation(redirect)
          } else {
            // If router is not yet ready, store the URL.
            // This handles the cold start scenario where the event fires very early.
            Sentry.addBreadcrumb({
              category: 'deep-link',
              message: `Router not yet ready, queuing deep link: ${redirect}`,
              level: 'debug',
              data: { route: redirect }
            })
            initialRouteLocation = redirect
          }
        })
      }
    })
    setOnUrlActionListener((url) => {
      Sentry.addBreadcrumb({
        category: 'deep-link',
        message: `Push notification URL action: ${url}`,
        level: 'debug',
        data: { url }
      })
      middlewareDeepLinkMobile(url, 'push-link').then((redirect) => {
        Sentry.addBreadcrumb({
          category: 'deep-link',
          message: `Redirecting from Push notification URL action: ${url}`,
          level: 'debug',
          data: { redirect }
        })
        if (isRouterReady) {
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Router is already ready, handling deep link immediately: ${redirect}`,
            level: 'debug',
            data: { route: redirect }
          })
          handleDeepLinkNavigation(redirect)
        } else {
          // If router is not yet ready, store the URL.
          // This handles the cold start scenario where the event fires very early.
          Sentry.addBreadcrumb({
            category: 'deep-link',
            message: `Router not yet ready, queuing deep link: ${redirect}`,
            level: 'debug',
            data: { route: redirect }
          })
          initialRouteLocation = redirect
        }
      })
    })
  }
}

async function handleForceUpdateCheck(
  appStatusStore: Store<STORE_ID.APP_STATUS, AppStatusState, unknown, { checkAppVersion(): Promise<void> }>
): Promise<boolean> {
  try {
    if (!isRouterReady) {
      return false
    }
    if (!router.currentRoute.value) {
      return false
    }
    await appStatusStore.checkAppVersion()
    // After checking, if an update is required, navigate to the blocking page
    if (appStatusStore.updateRequired && router.currentRoute.value.path !== '/update-required') {
      // Use a replace to prevent the user from being able to go back
      if (isRouterReady) {
        handleForceUpdateNavigation({ path: '/update-required' })
        return true
      }
    } else if (!appStatusStore.updateRequired && router.currentRoute.value.path === '/update-required') {
      // If the user is on the update page and no update is required, redirect them to home
      if (isRouterReady) {
        handleForceUpdateNavigation({ path: '/home' })
        return true
      }
    }
  } catch (error) {
    console.error('Error checking app version:', error)
  }
  return false
}

function initAppStateListener() {
  if (isPlatform('capacitor')) {
    const appStatusStore = useAppStatusStore()

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // When the app becomes active again, check the version status
        handleForceUpdateCheck(appStatusStore).catch((error) => {
          console.warn('Error checking app version:', JSON.stringify(error))
        })
      }
    })
  }
}

if (isPlatform('capacitor')) {
  initUrlOpenListener()
  initPushOpenListener()
}

app.directive('safe-html', safeHtmlDirective)
app.directive('trusted-html', trustedHtmlDirective)

router.isReady().then(async () => {
  await stripeBootstrapPromise
  // Mark the router as ready
  isRouterReady = true
  console.info('App is ready, mounting Vue instance')
  if (isPlatform('capacitor') && (isPlatform('android') || isPlatform('ios'))) {
    // Both native bars intentionally use light backgrounds with dark content.
    void SystemBars.setStyle({ bar: SystemBarType.StatusBar, style: SystemBarsStyle.Light })
    void SystemBars.setStyle({ bar: SystemBarType.NavigationBar, style: SystemBarsStyle.Light })
  }
  // reset the app status store
  const appStatusStore = useAppStatusStore()
  appStatusStore.$reset()
  app.mount('#app')

  // Check if force update is required on mobile
  let forceUpdateRequired = false
  if (isPlatform('capacitor')) {
    forceUpdateRequired = await handleForceUpdateCheck(appStatusStore)
  }
  // If an initial deep link was captured before the router was ready, handle it now.
  if (initialRouteLocation) {
    Sentry.addBreadcrumb({
      category: 'deep-link',
      message: `Initial deep link detected, handling now: ${initialRouteLocation}`,
      level: 'debug',
      data: { route: initialRouteLocation }
    })
    // If a force update is required, do not handle the deep link navigation
    if (!forceUpdateRequired) {
      handleDeepLinkNavigation(initialRouteLocation)
    }
    initialRouteLocation = null // Clear it after handling
  }

  // Add listener to handle force update check on app resume
  if (isPlatform('capacitor')) {
    initAppStateListener()
  }
  const initAppListeners = useAppListeners()
  initAppListeners().catch((error) => {
    console.error('Error initializing app listeners', error)
  })
  const tracking = useTracking()
  if (isPlatform('capacitor')) {
    await tracking.initialiseTrackingAppDetails()
  }
  tracking.identifyBrowser()
  const authStore = useAuthStore()
  if (authStore.authUser?.id) {
    tracking.identifyUser(authStore.authUser)
  } else {
    const userStore = useUserStore()
    tracking.identifyUser(userStore.getUserDetails)
  }

  // --- Retrieve and Add Native Startup/Resume Data to Sentry ---
  // This section runs after the app has mounted and Sentry is initialized.
  try {
    // Attempt to retrieve data stored during a cold app launch.
    const { value: startupDataJson } = await Preferences.get({ key: 'grilldStartupData' })
    if (startupDataJson) {
      const startupData = JSON.parse(startupDataJson)
      Sentry.addBreadcrumb({
        level: 'info',
        category: `native.startup.${startupData.launchType}`,
        message: `Native app launch detected`,
        data: startupData // Add the entire native data object to the breadcrumb
      })
      // Clear the data from native storage once it's been consumed by Sentry.
      await Preferences.remove({ key: 'grilldStartupData' })
    }

    // Attempt to retrieve data stored during an app resume via universal link.
    const { value: resumeDataJson } = await Preferences.get({ key: 'grilldResumeData' })
    if (resumeDataJson) {
      const resumeData = JSON.parse(resumeDataJson)
      Sentry.addBreadcrumb({
        level: 'info',
        category: `native.resume.${resumeData.launchType}`,
        message: `Native app resume detected`,
        data: resumeData
      })
      // Clear the data from native storage once consumed.
      await Preferences.remove({ key: 'grilldResumeData' })
    }
  } catch (error: any) {
    // Capture any errors that occur during the retrieval or parsing of native data.
    console.error('Error handling native startup/resume data:', error)
    Sentry.captureException(error)
  }
})
